import React, { useState } from "react";
import { useForm } from "react-hook-form";
import OBMatrix, { OBMatrixController, OBRule } from "./OBMatrix";
import Button from "../../Core/Button/Button";

const OBMatrixExample: React.FC = () => {
	const [basicRules, setBasicRules] = useState<OBRule[]>([
		{
			mode: "weekday",
			weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
			start: "17:00",
			end: "22:00",
			salarySupplement: 50,
			invoiceSupplement: 25,
			salaryType: null,
		},
		{
			mode: "weekday",
			weekdays: ["saturday", "sunday"],
			start: "00:00",
			end: "23:59",
			salarySupplement: 100,
			invoiceSupplement: 50,
			salaryType: "Helgtillägg",
		},
	]);

	const [conflictingRules, setConflictingRules] = useState<OBRule[]>([
		{
			mode: "weekday",
			weekdays: ["monday", "tuesday", "wednesday"],
			start: "18:00",
			end: "22:00",
			salarySupplement: 50,
			invoiceSupplement: 25,
			salaryType: null,
		},
		{
			mode: "weekday",
			weekdays: ["monday", "wednesday", "friday"],
			start: "20:00",
			end: "23:00",
			salarySupplement: 75,
			invoiceSupplement: 30,
			salaryType: null,
		},
	]);

	const [nightShiftRules, setNightShiftRules] = useState<OBRule[]>([
		{
			mode: "weekday",
			weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
			start: "22:00",
			end: "06:00",
			salarySupplement: 80,
			invoiceSupplement: 40,
			salaryType: "Natt-OB",
		},
	]);

	const [specialDateRules, setSpecialDateRules] = useState<OBRule[]>([
		{
			mode: "date",
			name: "Julafton",
			date: "2024-12-24",
			start: "00:00",
			end: "23:59",
			salarySupplement: 150,
			invoiceSupplement: 75,
			salaryType: "Helgdag",
		},
		{
			mode: "date",
			name: "Nyårsafton",
			date: "2024-12-31",
			start: "00:00",
			end: "23:59",
			salarySupplement: 150,
			invoiceSupplement: 75,
			salaryType: "Helgdag",
		},
		{
			mode: "date",
			name: "Midsommarafton",
			date: "2024-06-21",
			start: "00:00",
			end: "23:59",
			salarySupplement: 120,
			invoiceSupplement: 60,
			salaryType: "Helgdag",
		},
	]);

	// React Hook Form example
	interface FormData {
		obRules: OBRule[];
		contractName: string;
	}

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			contractName: "",
			obRules: [
				{
					mode: "weekday",
					weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
					start: "18:00",
					end: "22:00",
					salarySupplement: 60,
					invoiceSupplement: 30,
					salaryType: null,
				},
			],
		},
	});

	const onSubmit = (data: FormData) => {
		console.log("Form submitted:", data);
		alert(`Form submitted with ${data.obRules.length} OB rules. Check console for details.`);
	};

	return (
		<div className="space-y-8">
			<div>
				<h3 className="text-lg font-semibold mb-4">OBMatrix Component Examples</h3>

				<div className="space-y-8">
					{/* Basic Example */}
					<div>
						<h4 className="text-md font-medium mb-2">Basic OB Rules</h4>
						<p className="text-sm text-gray-600 mb-4">
							Standard weekday evening and weekend rules without conflicts
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<OBMatrix value={basicRules} onChange={setBasicRules} />
						</div>
					</div>

					{/* Conflicting Rules Example */}
					<div>
						<h4 className="text-md font-medium mb-2">Conflicting Rules Example</h4>
						<p className="text-sm text-gray-600 mb-4">
							Rules with overlapping time ranges and weekdays will show a warning
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<OBMatrix value={conflictingRules} onChange={setConflictingRules} />
						</div>
					</div>

					{/* Night Shift Example */}
					<div>
						<h4 className="text-md font-medium mb-2">Night Shift Rules</h4>
						<p className="text-sm text-gray-600 mb-4">
							Rules that span overnight (end time before start time)
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<OBMatrix value={nightShiftRules} onChange={setNightShiftRules} />
						</div>
					</div>

					{/* Special Date Example */}
					<div>
						<h4 className="text-md font-medium mb-2">Special Date Rules (Date Mode)</h4>
						<p className="text-sm text-gray-600 mb-4">
							Rules for specific dates like holidays using date mode. Notice the different columns
							for Name and Date instead of Weekdays.
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<OBMatrix value={specialDateRules} onChange={setSpecialDateRules} mode="date" />
						</div>
					</div>

					{/* Disabled State */}
					<div>
						<h4 className="text-md font-medium mb-2">Disabled State</h4>
						<p className="text-sm text-gray-600 mb-4">Matrix in read-only/disabled mode</p>
						<div className="border border-gray-300 rounded p-4 bg-gray-50">
							<OBMatrix value={basicRules} onChange={() => {}} disabled={true} />
						</div>
					</div>

					{/* Empty State */}
					<div>
						<h4 className="text-md font-medium mb-2">Empty State</h4>
						<p className="text-sm text-gray-600 mb-4">
							Matrix starts with one empty row when no rules are provided
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<OBMatrix value={[]} onChange={() => {}} />
						</div>
					</div>

					{/* React Hook Form Example */}
					<div>
						<h4 className="text-md font-medium mb-2">React Hook Form Integration</h4>
						<p className="text-sm text-gray-600 mb-4">
							Using OBMatrixController for seamless React Hook Form integration
						</p>
						<div className="border border-gray-300 rounded p-4 bg-white">
							<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
								<OBMatrixController control={control} name="obRules" />

								<div className="pt-4 border-t border-gray-200">
									<Button type="submit" width="200px">
										Submit Form
									</Button>
								</div>
							</form>
						</div>
						<div className="mt-4 p-4 bg-gray-50 rounded border border-gray-200">
							<p className="text-xs font-mono text-gray-700">
								<strong>Usage:</strong>
								<br />
								<code className="block mt-2 whitespace-pre text-xs">
									{`const { control } = useForm<FormData>();

<OBMatrixController
  control={control}
  name="obRules"
/>`}
								</code>
							</p>
						</div>
					</div>

					{/* Usage Notes */}
					<div className="bg-blue-50 border border-blue-200 rounded p-4">
						<h4 className="text-md font-semibold mb-2">Usage Notes</h4>
						<ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
							<li>
								<strong>Two modes available:</strong> "weekday" (default) for recurring weekly
								rules, "date" for specific dates
							</li>
							<li>
								<strong>Weekday mode:</strong> Click on weekday buttons to toggle selection
							</li>
							<li>Continuous weekday ranges are displayed as "Mon - Fri"</li>
							<li>Non-continuous days are shown as comma-separated: "Mon, Wed, Fri"</li>
							<li>
								<strong>Date mode:</strong> Enter a name (e.g., "Julafton") and select a specific
								date
							</li>
							<li>Both supplements are optional and can be set to 0 or left empty</li>
							<li>Salary type field is optional</li>
							<li>Conflicts are detected but do not block submission</li>
							<li>Supports overnight shifts (e.g., 22:00 to 06:00)</li>
							<li>At least one rule is required</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OBMatrixExample;
